import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { useIsMobileSm } from '../../hooks/useMediaQuery';

const FORMATS = [
  { key: 'ods', labelKey: 'exportODS' },
  { key: 'csv', labelKey: 'exportCSV' },
  { key: 'doc', labelKey: 'exportDOC' },
  { key: 'pdf', labelKey: 'exportPDF' },
];

/**
 * Export dropdown — desktop dropdown; mobile bottom sheet (avoids overlap on small screens).
 */
export function ExportMenu({
  open,
  onOpenChange,
  disabled = false,
  buttonLabel,
  labels,
  onExport,
  align = 'left',
  placement = 'down',
}) {
  const rootRef = useRef(null);
  const isMobile = useIsMobileSm();

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        onOpenChange(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    if (!isMobile) {
      document.addEventListener('pointerdown', onPointerDown);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange, isMobile]);

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  const positionClass =
    placement === 'up' ? 'bottom-full mb-1.5' : 'top-full mt-1.5';
  const alignClass = align === 'right' ? 'right-0' : 'left-0';

  const menuTitle = buttonLabel || labels.export;

  const formatButtons = FORMATS.map(({ key, labelKey }) => (
    <button
      key={key}
      type="button"
      role="menuitem"
      className="block w-full rounded-lg px-4 py-3 text-left text-sm font-medium text-text transition-colors hover:bg-primary-light hover:text-primary focus-visible:bg-primary-light focus-visible:text-primary focus-visible:outline-none dark:hover:bg-primary/25 sm:py-2.5 sm:rounded-none"
      onClick={() => {
        onExport(key);
        onOpenChange(false);
      }}
    >
      {labels[labelKey] || key.toUpperCase()}
    </button>
  ));

  const mobileSheet =
    open &&
    isMobile &&
    createPortal(
      <>
        <button
          type="button"
          className="fixed inset-0 z-[80] bg-black/45"
          aria-label="Close"
          onClick={() => onOpenChange(false)}
        />
        <div
          role="menu"
          className="fixed inset-x-0 bottom-0 z-[90] rounded-t-2xl border border-border bg-card px-4 pt-4 pb-safe shadow-card"
        >
          <p className="mb-2 text-sm font-semibold text-text">{menuTitle}</p>
          <div className="space-y-1">{formatButtons}</div>
        </div>
      </>,
      document.body
    );

  return (
    <div ref={rootRef} className={`relative shrink-0 ${open && !isMobile ? 'z-50' : ''}`}>
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => onOpenChange(!open)}
      >
        {menuTitle}
      </Button>
      {open && !isMobile && (
        <div
          role="menu"
          className={`absolute ${alignClass} ${positionClass} z-50 min-w-[9.5rem] overflow-hidden rounded-lg border border-border bg-card py-1 shadow-lg ring-1 ring-black/5 dark:bg-card dark:ring-white/10`}
        >
          {formatButtons}
        </div>
      )}
      {mobileSheet}
    </div>
  );
}
